import Raven from 'raven-js';
import { useCallback, useEffect, useMemo } from 'react';
import { submitInteraction } from 'self-service-api/api/submitInteraction';
import { track } from '../_core/common/eventTracking/tracker';
import { openUpgradePageInNewTab } from '../_core/utils/upgradePageRedirect';
import { UpgradePointTooltip } from './UpgradePointTooltip';
import useErrorAlert from './useErrorAlert';
import { getUpgradeConfigTrackingProperties, getUpgradePointPromise, updateViewedUpgradePointsCache, warnArchivedUpgradePointUse } from './utils';
import { useUpgradePointTooltipData } from './useUpgradePointTooltipData';
import { jsx as _jsx } from "react/jsx-runtime";
const logUseUpgradePointTryCatchError = ({
  customHookName = 'useUpgradePoint',
  error,
  message,
  upgradeKey
}) => {
  if (error instanceof Error) {
    Raven.captureException(new Error(`${customHookName} tryCatch error: ${message}`), {
      extra: {
        message: error.message,
        upgradeKey
      }
    });
  }
};
/**
 * ## Upgrade Management System
 *
 * `useUpgradePoint` takes your upgrade key as an argument and returns a callback
 * to open the associated upgrade page. Upgrade keys can be created and managed in the
 * [Upgrade Management System](<https://tools.hubteamqa.com/ums>) (`go/ums`).
 *
 * @example
 * ```tsx
 * const YourComponent = () => {
 *  const { openUpgradePage } = useUpgradePoint('your-upgrade-key');
 *
 *  return <Button onClick={openUpgradePage} />;
 * }
 * ```
 */
export function useUpgradePoint(upgradeKey, isFeatureLocked = true) {
  const {
    setShowErrorAlert
  } = useErrorAlert();
  const upgradePointPromise = useMemo(() => {
    return getUpgradePointPromise({
      upgradeKey,
      includePurchaseMotions: false
    });
  }, [upgradeKey]);

  // Fetch upgrade config and track view
  useEffect(() => {
    if (isFeatureLocked && upgradeKey) {
      upgradePointPromise.then(upgradeConfig => {
        if (!upgradeConfig) {
          return;
        }
        updateViewedUpgradePointsCache(upgradeKey, () => {
          track('interaction', Object.assign({
            action: 'viewed upgrade point'
          }, getUpgradeConfigTrackingProperties(upgradeConfig)));
          if (upgradeConfig.status === 'ARCHIVED') {
            warnArchivedUpgradePointUse(upgradeConfig.upgradeKey);
          }
        });
      }).catch(err => {
        console.error(err);
      });
    }
  }, [isFeatureLocked, upgradeKey, upgradePointPromise]);

  // Track click and set state
  const openUpgradePage = useCallback(() => {
    try {
      if (!isFeatureLocked) {
        return;
      }
      openUpgradePageInNewTab(upgradeKey);
      upgradePointPromise.then(upgradeConfig => {
        if (!upgradeConfig) return;
        track('interaction', Object.assign({
          action: 'clicked upgrade point'
        }, getUpgradeConfigTrackingProperties(upgradeConfig)));
        submitInteraction(upgradeKey).catch(error => Raven.captureException(Error('Failed to submit interaction in openUpgradePage'), {
          extra: {
            message: error.message,
            upgradeKey
          }
        }));
      }).catch(__error => {
        setShowErrorAlert(true);
      });
    } catch (error) {
      setShowErrorAlert(true);
      logUseUpgradePointTryCatchError({
        error,
        message: 'An error occured in defining openUpgradePage',
        upgradeKey
      });
    }
  }, [isFeatureLocked, setShowErrorAlert, upgradeKey, upgradePointPromise]);
  const UpgradePointTooltipWithUpgradeKey = ({
    children
  }) => {
    return /*#__PURE__*/_jsx(UpgradePointTooltip, {
      upgradeKey: upgradeKey,
      enableTooltip: isFeatureLocked,
      children: children
    });
  };
  const upgradePointTooltipData = useUpgradePointTooltipData(upgradeKey, isFeatureLocked);
  return {
    openUpgradePage,
    UpgradePointTooltip: UpgradePointTooltipWithUpgradeKey,
    upgradePointTooltipData
  };
}